import { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import strings from "../../i18n/language";
import { getActivitiesAction } from "../../redux/actions/activity";
import dayjs from "dayjs";
import ContactlessRoundedIcon from "@mui/icons-material/ContactlessRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import ActivityDelete from "../../dialog/ActivityDelete";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.38)';

const ROW_PER_PAGE = 10;

const ACTION_FILTERS = [
  { value: null, label: strings.LABEL_ACTIVITIES_FILTER_ALL },
  { value: "point", label: strings.LABEL_ACTIVITIES_FILTER_POINT },
  { value: "lost_point", label: strings.LABEL_ACTIVITIES_FILTER_LOST_POINT },
  { value: "gift", label: strings.LABEL_ACTIVITIES_FILTER_GIFT },
  { value: "lost_gift", label: strings.LABEL_ACTIVITIES_FILTER_LOST_GIFT },
];

const ACTION_META = {
  point:      { bg: 'rgba(59,149,232,0.14)',  color: '#3b95e8' },
  gift:       { bg: 'rgba(91,191,181,0.14)',  color: '#5bbfb5' },
  lost_point: { bg: 'rgba(139,159,212,0.14)', color: '#8b9fd4' },
  lost_gift:  { bg: 'rgba(139,159,212,0.10)', color: '#8b9fd4' },
};

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  overflow: 'hidden',
};

const FilterChip = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    background: active ? 'rgba(59,149,232,0.15)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active ? 'rgba(59,149,232,0.5)' : BORDER}`,
    borderRadius: 8,
    color: active ? BRAND : MUTED,
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    padding: '5px 12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    letterSpacing: 0.2,
  }}>
    {label}
  </button>
);

const ActionBadge = ({ action }) => {
  const meta = ACTION_META[action] || { bg: GLASS, color: MUTED };
  const label = ACTION_FILTERS.find((f) => f.value === action)?.label || action;
  return (
    <span style={{
      background: meta.bg,
      color: meta.color,
      borderRadius: 6,
      padding: '3px 9px',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: 0.3,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
};

const ColHeader = ({ children, style }) => (
  <th style={{
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER}`,
    ...style,
  }}>
    {children}
  </th>
);

const Td = ({ children, style }) => (
  <td style={{
    padding: '13px 16px',
    fontSize: 13,
    color: 'rgba(232,240,254,0.75)',
    borderBottom: `1px solid rgba(255,255,255,0.04)`,
    verticalAlign: 'middle',
    ...style,
  }}>
    {children}
  </td>
);

const DAY_NAMES = () => [
  null, strings.LABEL_DAY_MON, strings.LABEL_DAY_TUE, strings.LABEL_DAY_WED,
  strings.LABEL_DAY_THU, strings.LABEL_DAY_FRI, strings.LABEL_DAY_SAT, strings.LABEL_DAY_SUN,
];

const dateInputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  color: '#e8f0fe',
  fontSize: 12,
  padding: '4px 8px',
  colorScheme: 'dark',
};

const RemovableChip = ({ label, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(59,149,232,0.15)', border: '1px solid rgba(59,149,232,0.5)',
    borderRadius: 8, color: BRAND, fontSize: 12, fontWeight: 600, padding: '4px 8px 4px 12px',
  }}>
    {label}
    <button onClick={onRemove} style={{ background: 'none', border: 'none', color: BRAND, cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
  </span>
);

const ActivityList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Filters live in the URL: the dashboard heatmap links here with them
  const filters = {
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || '',
    local: searchParams.get('local') || '',
    localLabel: searchParams.get('localLabel') || '',
    weekday: searchParams.get('weekday') || '',
    hourFrom: searchParams.get('hourFrom') || '',
    hourTo: searchParams.get('hourTo') || '',
  };
  const filtersKey = searchParams.toString();
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState(searchParams.get('action') || null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const dispatch = useDispatch();

  const setFilters = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const activities = useSelector((state) => state.activity.activities);
  const isLoadingGetActivities = useSelector((state) => state.activity.isLoadingGetActivities);
  const total = useSelector((state) => state.activity.total);
  const selectedCompany = useSelector((state) => state.login.selectedCompany);
  const user = useSelector((state) => state.user.user);

  const refresh = () => {
    dispatch(getActivitiesAction(ROW_PER_PAGE, page * ROW_PER_PAGE, actionFilter, selectedCompany?.id, filters));
  };

  useEffect(() => {
    dispatch(getActivitiesAction(ROW_PER_PAGE, 0, actionFilter, selectedCompany?.id, filters));
    // filtersKey stands for the filters object, rebuilt on every render
  }, [dispatch, actionFilter, selectedCompany, filtersKey]);

  const handleFilterChange = (value) => {
    setActionFilter(value);
    setPage(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    dispatch(getActivitiesAction(ROW_PER_PAGE, newPage * ROW_PER_PAGE, actionFilter, selectedCompany?.id, filters));
  };

  const dayNames = DAY_NAMES();
  const timeChip = filters.weekday || filters.hourFrom || filters.hourTo
    ? [
      filters.weekday ? dayNames[Number(filters.weekday)] : null,
      filters.hourFrom || filters.hourTo
        ? `${String(filters.hourFrom || 0).padStart(2, '0')}:00–${String(filters.hourTo || 24).padStart(2, '0')}:00`
        : null,
    ].filter(Boolean).join(' · ')
    : null;
  const hasFilters = filters.from || filters.to || filters.local || timeChip;

  const getDetailText = (activity) => {
    if (activity.action === "lost_point") return `${strings.LABEL_ACTIVITIES_LOST_POINT} ${activity.targetUser?.display_name}`;
    if (activity.action === "lost_gift") return `${strings.LABEL_ACTIVITIES_LOST_GIFT} ${activity.targetUser?.display_name}`;
    if (activity.action === "gift") return `${strings.LABEL_ACTIVITIES_GIFT_10} ${activity.targetUser?.display_name}`;
    if (activity.action === "point") {
      const from = activity.user_id !== activity.target_id ? ` de parte de ${activity.sourceUser?.display_name}` : '';
      return `${strings.LABEL_ACTIVITIES_POINT} ${activity.targetUser?.display_name}${from}`;
    }
    return '—';
  };

  const getInfoText = (activity) => {
    if (activity.action === "lost_point") return `${strings.LABEL_ACTIVITIES_LOST_POINT} ${activity.targetUser?.id}`;
    if (activity.action === "lost_gift") return `${strings.LABEL_ACTIVITIES_LOST_GIFT} ${activity.targetUser?.id}`;
    if (activity.action === "gift") return `${strings.LABEL_ACTIVITIES_GIFT_10} ${activity.targetUser?.id}`;
    if (activity.action === "point") {
      const from = activity.user_id !== activity.target_id ? ` de parte de ${activity.sourceUser?.id}` : '';
      return `${strings.LABEL_ACTIVITIES_POINT} ${activity.targetUser?.id}${from}`;
    }
    return '';
  };

  if (!activities) return null;

  const totalPages = Math.ceil(total / ROW_PER_PAGE);

  return (
    <Box sx={{ width: "100%" }}>
      <div style={card}>

        {/* Header + Filters */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <Typography style={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe', letterSpacing: 0.2 }}>
              {strings.LABEL_ACTIVITIES_TITLE || 'Actividad'}
            </Typography>
            {total > 0 && (
              <span style={{ fontSize: 12, color: MUTED }}>
                {total} {strings.LABEL_ACTIVITIES_TOTAL || 'registros'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ACTION_FILTERS.map((f) => (
              <FilterChip
                key={f.value ?? 'all'}
                label={f.label}
                active={actionFilter === f.value}
                onClick={() => handleFilterChange(f.value)}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 12, color: MUTED }}>{strings.LABEL_ACTIVITIES_FROM}</span>
            <input type="date" value={filters.from} onChange={(e) => setFilters({ from: e.target.value })} style={dateInputStyle} />
            <span style={{ fontSize: 12, color: MUTED }}>{strings.LABEL_ACTIVITIES_TO}</span>
            <input type="date" value={filters.to} onChange={(e) => setFilters({ to: e.target.value })} style={dateInputStyle} />
            {filters.local && (
              <RemovableChip
                label={filters.local === 'none' ? strings.LABEL_ACTIVITIES_NO_LOCAL : (filters.localLabel || `${strings.LABEL_ACTIVITIES_LOCAL} ${filters.local}`)}
                onRemove={() => setFilters({ local: null, localLabel: null })}
              />
            )}
            {timeChip && (
              <RemovableChip label={timeChip} onRemove={() => setFilters({ weekday: null, hourFrom: null, hourTo: null })} />
            )}
            {hasFilters && (
              <button
                onClick={() => setFilters({ from: null, to: null, local: null, localLabel: null, weekday: null, hourFrom: null, hourTo: null })}
                style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                {strings.LABEL_ACTIVITIES_CLEAR_FILTERS}
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <ColHeader>{strings.LABEL_ACTIVITIES_DATE}</ColHeader>
                <ColHeader style={{ width: 110 }}>Action</ColHeader>
                <ColHeader style={{ width: 70 }}>{strings.LABEL_ACTIVITIES_POINTS}</ColHeader>
                <ColHeader>{strings.LABEL_ACTIVITIES_DETAIL}</ColHeader>
                <ColHeader>{strings.LABEL_ACTIVITIES_LOCAL}</ColHeader>
                <ColHeader style={{ width: user?.admin ? 70 : 40 }}></ColHeader>
              </tr>
            </thead>
            <tbody>
              {isLoadingGetActivities && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 40 }}>
                    <CircularProgress size={24} sx={{ color: BRAND }} />
                  </td>
                </tr>
              )}
              {!isLoadingGetActivities && activities.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 40, fontSize: 13, color: MUTED }}>
                    {strings.LABEL_ACTIVITIES_NO_ACTIVITIES}
                  </td>
                </tr>
              )}
              {activities.map((activity) => (
                <tr key={activity.id} style={{ transition: 'background 0.1s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <Td style={{ color: MUTED, whiteSpace: 'nowrap', fontSize: 12 }}>
                    {dayjs(activity.date).format("LLL")}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ActionBadge action={activity.action} />
                      {activity.user_id === activity.target_id && (
                        <ContactlessRoundedIcon style={{ fontSize: 15, color: MUTED }} />
                      )}
                    </div>
                  </Td>
                  <Td style={{ fontWeight: 700, whiteSpace: 'nowrap', color: activity.action === "lost_point" ? "#e05c5c" : "#e8f0fe" }}>
                    {activity.action === "point" ? `+${activity.points ?? 1}`
                      : activity.action === "lost_point" ? `−${activity.points ?? 1}` : ""}
                  </Td>
                  <Td>{getDetailText(activity)}</Td>
                  <Td style={{ color: MUTED }}>
                    {activity.Address ? activity.Address.label : strings.LABEL_ACTIVITIES_NO_LOCAL}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tooltip title={getInfoText(activity)} placement="left">
                        <InfoOutlinedIcon style={{ fontSize: 15, color: MUTED, cursor: 'help', display: 'block' }} />
                      </Tooltip>
                      {user?.admin && (
                        <Tooltip title={strings.LABEL_BUTTON_DELETE} placement="left">
                          <DeleteOutlineRoundedIcon
                            style={{ fontSize: 15, color: MUTED, cursor: 'pointer', display: 'block' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#e05c5c'}
                            onMouseLeave={(e) => e.currentTarget.style.color = MUTED}
                            onClick={() => { setDeleteId(activity.id); setDeleteOpen(true); }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '12px 24px' }}>
            <span style={{ fontSize: 12, color: MUTED }}>
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                color: page === 0 ? 'rgba(255,255,255,0.15)' : '#e8f0fe',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <NavigateBeforeRoundedIcon style={{ fontSize: 18 }} />
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages - 1}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${BORDER}`,
                borderRadius: 8,
                color: page >= totalPages - 1 ? 'rgba(255,255,255,0.15)' : '#e8f0fe',
                cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <NavigateNextRoundedIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}
      </div>
      <ActivityDelete
        open={deleteOpen}
        setOpen={setDeleteOpen}
        activityDeleteId={deleteId}
        onDeleted={refresh}
      />
    </Box>
  );
};

export default ActivityList;
